import axios, { AxiosError } from 'axios';
import type { InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { getAuthToken, removeAuthToken } from '../helper/helper';
// import { ToastContainer, toast } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';

interface ErrorResponseData {
  message?: string;
  code?: number;
}
const APP_PUBLIC_URL = import.meta.env.BASE_URL || "/";
const GUEST_ACCESS_TOKEN = (import.meta.env.VITE_GUEST_ACCESS_TOKEN || "").trim();

export const Api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
});

Api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    config.headers = config.headers || {};

    const token = getAuthToken();

    // Prefer logged-in token. If not present, optionally use a configured guest token
    // to fetch protected read data without forcing user login.
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else if (GUEST_ACCESS_TOKEN) {
      config.headers.Authorization = `Bearer ${GUEST_ACCESS_TOKEN}`;
    } else {
      delete config.headers.Authorization;
    }

    return config;
  },
  (error: AxiosError) => {
    console.error("Request error:", error);
    return Promise.reject(error);
  }
);

Api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error: AxiosError<ErrorResponseData>) => {
    if (error.response) {
      const responseData = error.response.data;
      const hasToken = !!getAuthToken();

      if (responseData?.message === "Invalid or expired token") {
        // For guests (no token), don't force redirect/reload.
        if (!hasToken) {
          return Promise.reject(error);
        }
        removeAuthToken();
        window.location.href = `${APP_PUBLIC_URL}#/`;
        return Promise.reject(error);
      }
      else if (responseData?.code === 401) {
        // For guests (no token), allow requests to fail naturally without forced navigation.
        if (!hasToken) {
          return Promise.reject(error);
        }
        removeAuthToken();
        window.location.href = `${APP_PUBLIC_URL}#/`;
        return Promise.reject(error);
      }
      else if (responseData?.message === "User not found") {
        if (!hasToken) {
          return Promise.reject(error);
        }
        removeAuthToken();
        window.location.href = `${APP_PUBLIC_URL}#/`;
        return Promise.reject(error);
      }
      else {
        // toast.error(responseData?.message || "An error occurred");

        // Server returned a response with an error status code
        console.error(
          `API Error: ${error.response.status} - ${responseData?.message || error.response.statusText}`
        );
        return Promise.reject(error);
      }
    } else if (error.request) {
      // No response received from the server
      console.error("No response received:", error.request);
    } else {
      // Something else caused the error
      console.error("Error:", error.message);
    }

    // toast.error(error.message || "An unexpected error occurred");
    // Reject the promise to allow custom error handling elsewhere
    return Promise.reject(error);
  }
);